import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CLOUDFLARE_CONFIG, API_CONFIG } from '../config/api';
import apiService from '../services/apiService';
import { FiExternalLink, FiCopy } from 'react-icons/fi';
import './ManageProperties.css';

const PortfolioDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [portfolioData, setPortfolioData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageUploads, setImageUploads] = useState({
    heroImage: { file: null, status: '', uploading: false },
    image1: { file: null, status: '', uploading: false },
    image2: { file: null, status: '', uploading: false },
    image3: { file: null, status: '', uploading: false }
  });

  useEffect(() => {
    if (id) {
      fetchPortfolioDetails();
      fetchPortfolioProperties();
    }
  }, [id]);

  const fetchPortfolioDetails = async () => {
    try {
      setLoading(true);
      
      const data = await apiService.get(`/portfolios/${id}`);
      setPortfolioData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching portfolio details:', err);
      setError('Failed to load portfolio details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolioProperties = async () => {
    try {
      const data = await apiService.post(`/properties/admin/portfolio/${id}`, {});
      setProperties(data || []);
    } catch (err) {
      console.error('Error fetching portfolio properties:', err);
      // Don't set error state here, just log it
    }
  };

  const handleRemoveProperty = async (propertyId) => {
    if (!confirm('Are you sure you want to remove this property from the portfolio?')) {
      return;
    }

    try {
      await apiService.delete(`/properties/${propertyId}/portfolios`);
      // Refresh the properties list
      fetchPortfolioProperties();
      alert('Property removed from portfolio successfully!');
    } catch (err) {
      console.error('Error removing property from portfolio:', err);
      alert('Failed to remove property. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setPortfolioData({
      ...portfolioData,
      [field]: value
    });
  };

  const handleFileChange = (imageKey, file) => {
    setImageUploads({
      ...imageUploads,
      [imageKey]: { ...imageUploads[imageKey], file: file, status: '' }
    });
  };

  const uploadToCloudflare = async (imageKey, file) => {
    try {
      // Update status
      setImageUploads(prev => ({
        ...prev,
        [imageKey]: { ...prev[imageKey], status: 'Uploading to Cloudflare...', uploading: true }
      }));

      let imageLink = null;

      // Hero image: get upload URL from backend, then upload to Cloudflare
      if (imageKey === 'heroImage') {
        const uploadUrlResponse = await apiService.post(
          `/Cloudflare/upload-url?uploadType=images&portfolioId=${id}`
        );

        if (!uploadUrlResponse.uploadUrl) {
          throw new Error("Failed to get upload URL from backend");
        }

        const formData = new FormData();
        formData.append("file", file);

        const cloudflareResponse = await fetch(uploadUrlResponse.uploadUrl, {
          method: "POST",
          body: formData,
        });

        if (!cloudflareResponse.ok) {
          throw new Error(`Cloudflare upload failed: ${cloudflareResponse.status}`);
        }

        const cloudflareResult = await cloudflareResponse.json();
        if (cloudflareResult.success && cloudflareResult.result?.variants) {
          imageLink = cloudflareResult.result.variants[0];
        } else {
          throw new Error("Cloudflare upload response missing variants");
        }
      } else {
        // image1, image2, image3: use direct Cloudflare API
        const renamedFile = new File([file], `CBREX_${id}_${file.name}`, {
          type: file.type,
        });

        const cloudflareFormData = new FormData();
        cloudflareFormData.append("file", renamedFile);

        let cloudflareResponse = null;

        try {
          const directResponse = await fetch(CLOUDFLARE_CONFIG.IMAGES_ENDPOINT, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${CLOUDFLARE_CONFIG.API_TOKEN}`,
            },
            body: cloudflareFormData,
          });

          if (directResponse.ok) {
            cloudflareResponse = await directResponse.json();
          }
        } catch (error) {
          console.log('Direct upload failed (likely CORS), trying proxies...', error);
        }

        if (!cloudflareResponse) {
        const corsProxies = API_CONFIG.CORS_PROXIES;

          for (const proxy of corsProxies) {
            try {
              const response = await fetch(
                proxy + CLOUDFLARE_CONFIG.IMAGES_ENDPOINT,
                {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${CLOUDFLARE_CONFIG.API_TOKEN}`,
                  },
                  body: cloudflareFormData,
                }
              );

              if (response.ok) {
                cloudflareResponse = await response.json();
                break;
              }
            } catch (error) {
              console.log(`Proxy ${proxy} failed:`, error);
              continue;
            }
          }
        }

        if (!cloudflareResponse) {
          throw new Error("All CORS proxies failed");
        }

        if (cloudflareResponse.success && cloudflareResponse.result.variants) {
          imageLink = cloudflareResponse.result.variants[0];
        } else {
          throw new Error("Cloudflare upload failed");
        }
      }

      if (imageLink) {
        
        // Update portfolio data with new image link
        const linkField = imageKey === 'heroImage' ? 'heroImageLink' : 
                         imageKey === 'image1' ? 'image1Link' :
                         imageKey === 'image2' ? 'image2Link' : 'image3Link';
        
        // Update local state first
        const updatedPortfolioData = {
          ...portfolioData,
          [linkField]: imageLink
        };
        
        setPortfolioData(updatedPortfolioData);

        setImageUploads(prev => ({
          ...prev,
          [imageKey]: { 
            file: null, 
            status: 'Saving to database...', 
            uploading: true 
          }
        }));

        // Automatically save to database after successful Cloudflare upload
        try {
          const requestBody = {
            userId: 3,
            title: updatedPortfolioData.title || "",
            description: updatedPortfolioData.description || "",
            heroImageLink: updatedPortfolioData.heroImageLink || "",
            image1Link: updatedPortfolioData.image1Link || "",
            image2Link: updatedPortfolioData.image2Link || "",
            image3Link: updatedPortfolioData.image3Link || ""
          };

          await apiService.put(`/portfolios?id=${id}`, requestBody);

          setImageUploads(prev => ({
            ...prev,
            [imageKey]: { 
              file: null, 
              status: 'Upload and save successful!', 
              uploading: false 
            }
          }));

        } catch (saveError) {
          console.error('Error saving portfolio after image upload:', saveError);
          setImageUploads(prev => ({
            ...prev,
            [imageKey]: { 
              file: null, 
              status: 'Upload successful, but save failed!', 
              uploading: false 
            }
          }));
          throw saveError;
        }

        return imageLink;
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error(`Error uploading ${imageKey}:`, error);
      setImageUploads(prev => ({
        ...prev,
        [imageKey]: { ...prev[imageKey], status: 'Upload failed!', uploading: false }
      }));
      throw error;
    }
  };

  const handleImageUpload = async (imageKey) => {
    const file = imageUploads[imageKey].file;
    if (!file) return;

    try {
      await uploadToCloudflare(imageKey, file);
    } catch (error) {
      console.error(`Failed to upload ${imageKey}:`, error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const requestBody = {
        userId: 3,
        title: portfolioData.title || "",
        description: portfolioData.description || "",
        heroImageLink: portfolioData.heroImageLink || "",
        image1Link: portfolioData.image1Link || "",
        image2Link: portfolioData.image2Link || "",
        image3Link: portfolioData.image3Link || ""
      };

      await apiService.put(`/portfolios?id=${id}`, requestBody);
      
      alert('Portfolio updated successfully!');
      
    } catch (err) {
      console.error('Error updating portfolio:', err);
      alert('Failed to update portfolio. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = () => {
    const url = `${API_CONFIG.PUBLIC_SITE_BASE_URL}/portfolio/${id}?${portfolioData?.title?.replace(/\s+/g, '_') || ''}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        alert("Link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        alert("Failed to copy link");
      });
  };

  if (loading) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Portfolio Details</h1>
        </div>
        <div className="loading-state">
          <p>Loading portfolio details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Portfolio Details</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchPortfolioDetails} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!portfolioData) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Portfolio Details</h1>
        </div>
        <div className="error-state">
          <p>Portfolio not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          Portfolio Details{" "}
          {portfolioData?.title ? `- ${portfolioData.title}` : ``}
          <button
            onClick={() =>
              window.open(
                `${API_CONFIG.PUBLIC_SITE_BASE_URL}/portfolio/${id}?${portfolioData?.title?.replace(/\s+/g, '_') || ''}`,
                "_blank"
              )
            }
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#0066cc",
              display: "flex",
              alignItems: "center",
              padding: "5px",
              fontSize: "20px",
            }}
            title="View portfolio on public site"
          >
            <FiExternalLink />
          </button>
          <button
            onClick={handleCopyLink}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#0066cc",
              display: "flex",
              alignItems: "center",
              padding: "5px",
              fontSize: "20px",
            }}
            title="Copy portfolio link for email"
          >
            <FiCopy />
          </button>
        </h1>
      </div>

      <div className="content-container">
        {/* Top Section: Form fields on left, Hero image on right */}
        <div className="portfolio-top-section" style={{ display: 'flex', gap: '20px', marginBottom: '30px', alignItems: 'stretch' }}>
          <div className="portfolio-form-section" style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
            <div className="form-section" style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
              <div className="form-row">
                <label htmlFor="title">Title:</label>
                <input
                  type="text"
                  id="title"
                  className="form-input"
                  value={portfolioData.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </div>
              
              <div className="form-row" style={{ flex: '1' }}>
                <label htmlFor="description">Description:</label>
                <textarea
                  id="description"
                  className="form-textarea"
                  value={portfolioData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows="12"
                  style={{ flex: '1', minHeight: '200px' }}
                />
              </div>

              {/* Update Button */}
              <div className="form-actions" style={{ marginTop: '40px' }}>
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Updating...' : 'Update Portfolio'}
                </button>
              </div>
            </div>
          </div>

          {/* Hero Image Display */}
          <div className="portfolio-hero-section" style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
            <div className="form-section" style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
              <h3>Hero Image</h3>
              <div style={{ flex: '1', display: 'flex', flexDirection: 'column', minHeight: 0, marginBottom: '15px' }}>
                {portfolioData.heroImageLink ? (
                  <img 
                    src={portfolioData.heroImageLink} 
                    alt="Hero"
                    style={{ 
                      width: '100%', 
                      maxWidth: '400px', 
                      height: '100%',
                      maxHeight: '100%',
                      objectFit: 'cover',
                      border: '1px solid var(--cbre-border)',
                      borderRadius: '4px',
                      flex: '1'
                    }}
                  />
                ) : (
                  <div style={{ 
                    width: '100%', 
                    maxWidth: '400px',
                    height: '100%',
                    flex: '1',
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--cbre-text-secondary)'
                  }}>
                    No hero image
                  </div>
                )}
              </div>

              {/* Hero Image Upload */}
              <div className="form-row">
                <label htmlFor="heroImageUpload">Update Hero Image:</label>
                <div className="upload-controls">
                  <input
                    type="file"
                    id="heroImageUpload"
                    accept="image/*"
                    onChange={(e) => handleFileChange('heroImage', e.target.files[0])}
                    className="form-input"
                    style={{ marginBottom: '5px' }}
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('heroImage')}
                    disabled={!imageUploads.heroImage.file || imageUploads.heroImage.uploading}
                    className="btn-primary"
                  >
                    {imageUploads.heroImage.uploading ? 'Uploading...' : 'Upload'}
                  </button>
                  {imageUploads.heroImage.status && (
                    <span className="upload-status" style={{ marginLeft: '10px', fontSize: '14px' }}>
                      {imageUploads.heroImage.status}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Properties Section */}
        {properties.length > 0 && (
          <div className="properties-table" style={{ marginTop: '30px' }}>
            <h2 style={{ marginBottom: '20px', marginLeft: '16px', fontSize: '24px', fontWeight: '600' }}>Properties</h2>
            <table>
              <colgroup>
                <col style={{ width: '100px' }} />
                <col />
                <col style={{ width: '120px' }} />
                <col style={{ width: '150px' }} />
                <col style={{ width: '130px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '80px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Property Name</th>
                  <th>Category</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Published</th>
                  <th>Discrete List</th>
                  <th>Carousel Featured</th>
                  <th>Public Listing</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((property) => (
                  <tr 
                    key={property.id}
                    className={property.placeholderyn === 1 ? 'special-property-row' : ''}
                  >
                    <td>
                      <strong>{property.propertyReference || `CBRE-${property.id}`}</strong>
                    </td>
                    <td>
                      <Link
                        to={`/properties/${property.id}`}
                        className="property-link"
                      >
                        {property.listingTitle}
                        {property.placeholderyn === 1 && <span style={{ color: '#ff6b6b', fontSize: '20px', marginLeft: '8px' }}>*</span>}
                      </Link>
                      <p className="property-description">
                        {property.listingDescription}
                      </p>
                    </td>
                    <td>
                      {property.category}
                    </td>
                    <td>
                      <div className="location-info">
                        <div>{property.suburb}</div>
                        <div>{property.province}</div>
                      </div>
                    </td>
                    <td>
                      {property.propertyStatus}
                    </td>
                    <td>
                      <span
                        className={`active-badge ${
                          property.isActive ? "active" : "inactive"
                        }`}
                      >
                        {property.isActive ? "Yes" : "No"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`discrete-badge ${
                          property.isDiscrete ? "discrete" : "not-discrete"
                        }`}
                      >
                        {property.isDiscrete ? "Yes" : "No"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`featured-badge ${
                          property.isFeatured ? "featured" : "not-featured"
                        }`}
                      >
                        {property.isFeatured ? "Yes" : "No"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`listable-badge ${
                          property.isListable ? "listable" : "not-listable"
                        }`}
                      >
                        {property.isListable ? "Yes" : "No"}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handleRemoveProperty(property.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '4px',
                          fontSize: '16px'
                        }}
                        title="Remove property from portfolio"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioDetails;
